import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, KeyRound, Car, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BODYSHOP_IMG } from "@/lib/bodyshop";

const BG = "https://images.unsplash.com/photo-1654159866298-e3c8ee93e43b?crop=entropy&cs=srgb&fm=jpg&q=85";
const ACTIVITIES = [
  { key: "noleggio", to: "/cars", icon: KeyRound, title: "Noleggio auto", desc: "A giornata, weekend o settimana. Paghi online o al ritiro.", cta: "Prenota",
    img: "https://static.prod-images.emergentagent.com/jobs/37292f66-9b78-417c-bf5e-ca69d8f08ebe/images/2ab00dfdd75546599efb69cea1da71b150d3b61e4e705eed4b5971e626cd286a.jpeg" },
  { key: "vendita", to: "/vendita", icon: Car, title: "Vendita auto", desc: "Usato controllato e tagliandato, con ampia scelta di prezzi.", cta: "Vedi le auto", img: BG },
  { key: "carrozzeria", to: "/carrozzeria-pontedera", icon: Wrench, title: "Carrozzeria", desc: "Verniciatura a forno, grandine, sinistri, auto sostitutiva e assistenza legale gratuita.", cta: "Scopri", img: BODYSHOP_IMG.hero },
];
const Eyebrow = () => <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-5" data-testid="hero-eyebrow">Centro Auto Pontedera · dal 1970</div>;

const HeroStack = () => {
  const navigate = useNavigate();
  return (
    <section className="relative pt-20 overflow-hidden" data-testid="hero-v1">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
        <div className="max-w-4xl fade-up">
          <Eyebrow />
          <h1 className="font-display text-[1.7rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1.08]" data-testid="hero-activities">
            {ACTIVITIES.map((a, i) => (
              <Link key={a.key} to={a.to} className="group relative block w-fit sm:whitespace-nowrap" data-testid={`hero-activity-${a.key}`}>
                <span className={`${i === 1 ? "amber" : ""} group-hover:text-[#E63946] transition-colors`}>{a.title}.</span>
                <ArrowRight className="absolute -right-14 top-1/2 -translate-y-1/2 w-9 h-9 text-[#E63946] opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
              </Link>
            ))}
          </h1>
          <p className="font-display text-lg sm:text-2xl font-bold uppercase mt-8" data-testid="hero-tagline">Noleggia l'auto, <span className="amber">paga come vuoi.</span></p>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mt-3 max-w-xl">Noleggio con pagamento online o al ritiro, auto usate controllate e tagliandate, carrozzeria con verniciatura a forno, grandine e sinistri.</p>
          <div className="flex flex-wrap gap-3 mt-10">
            <Button onClick={() => navigate("/cars")} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-8 h-12 text-base font-semibold" data-testid="hero-cta">Scopri il Parco Auto <ArrowRight className="w-5 h-5 ml-2" /></Button>
            <Button onClick={() => navigate("/vendita")} variant="outline" className="rounded-full h-12 px-6 border-white/15 bg-transparent" data-testid="hero-cta-vendita">Auto in vendita</Button>
            <Button onClick={() => navigate("/carrozzeria-pontedera")} variant="outline" className="rounded-full h-12 px-6 border-white/15 bg-transparent" data-testid="hero-cta-carrozzeria">Carrozzeria</Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroPanels = () => {
  const [saleImg, setSaleImg] = useState(null);
  useEffect(() => { api.get("/sale-cars").then((r) => setSaleImg(r.data.find((c) => c.images?.length)?.images[0] || null)).catch(() => {}); }, []);
  return (
  <section className="relative pt-16 sm:pt-[92px]" data-testid="hero-v2">
    <h1 className="sr-only">Centro Auto Pontedera: noleggio auto, vendita auto e carrozzeria a Pontedera</h1>
    <div className="absolute top-20 sm:top-[122px] inset-x-0 z-10 flex justify-center pointer-events-none px-4">
      <div className="rounded-full glass border border-white/15 px-5 py-2 font-mono-tech text-[10px] sm:text-xs tracking-[0.3em] uppercase amber fade-up" data-testid="hero-eyebrow">Centro Auto Pontedera · dal 1970</div>
    </div>
    <div className="grid lg:grid-cols-3 lg:h-[calc(100vh-92px)] min-h-[560px]">
      {ACTIVITIES.map((a, i) => (
        <Link key={a.key} to={a.to} className="group relative min-w-0 overflow-hidden min-h-[300px] border-b lg:border-b-0 lg:border-r border-white/10 last:border-0" data-testid={`hero-activity-${a.key}`}>
          <img src={a.key === "vendita" && saleImg ? saleImg : a.img} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-70 group-hover:scale-105 transition-[opacity,transform] duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-6 lg:p-10 fade-up" style={{ animationDelay: `${i * 120}ms` }}>
            <div className="font-mono-tech text-xs tracking-[0.3em] amber mb-3">0{i + 1}</div>
            <div className="font-display text-[1.7rem] sm:text-4xl lg:text-[2.1rem] xl:text-[2.6rem] font-extrabold uppercase leading-none">{a.title}</div>
            <p className="text-sm sm:text-base text-slate-300 mt-4 max-w-sm">{a.desc}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-[#E63946] font-semibold group-hover:gap-3 transition-all">{a.cta} <ArrowRight className="w-5 h-5" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
  );
};

const HeroCards = () => (
  <section className="relative pt-20 overflow-hidden" data-testid="hero-v3">
    <div className="absolute inset-0">
      <img src={BG} alt="" className="w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/50" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
      <div className="text-center fade-up">
        <Eyebrow />
        <h1 className="font-display text-[1.9rem] sm:text-5xl lg:text-7xl font-extrabold tracking-tight uppercase leading-none">Tutto per la tua auto.<br /><span className="amber">In un solo posto.</span></h1>
        <p className="text-base sm:text-lg text-slate-300 mt-6 max-w-2xl mx-auto">A Pontedera dal 1970: noleggi, compri e ripari la tua auto con le stesse persone di fiducia.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-14" data-testid="hero-activities">
        {ACTIVITIES.map((a, i) => (
          <Link key={a.key} to={a.to} className="group min-w-0 rounded-3xl glass border border-white/10 hover:border-[#E63946]/60 p-6 lg:p-8 transition-colors fade-up" style={{ animationDelay: `${150 + i * 120}ms` }} data-testid={`hero-activity-${a.key}`}>
            <a.icon className="w-9 h-9 text-[#E63946] mb-6" />
            <div className="font-display text-[1.6rem] md:text-xl lg:text-2xl xl:text-[1.6rem] font-extrabold uppercase leading-none">{a.title}</div>
            <p className="text-sm text-muted-foreground mt-4">{a.desc}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold group-hover:text-[#E63946] transition-colors">{a.cta} <ArrowRight className="w-4 h-4" /></span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const HomeHero = ({ version }) => (version === "1" ? <HeroStack /> : version === "3" ? <HeroCards /> : <HeroPanels />);
